import { NgModule, inject } from '@angular/core';
import { CanActivateFn, RouterModule, Routes } from '@angular/router';
import { PERMISSIONS_V2_FEATURE_FLAG } from 'src/constants/feature-flags.constants';
import { TENANT_AUTHORIZATION_REQUIREMENTS } from 'src/constants/tenant-authorization.constants';
import { AuthService } from 'src/services/auth.service';

import { appRouterOptions } from './app-router-options';
import { AuthGuard } from './auth.guard';
import { CloseWindowComponent } from './components/close-window/close-window.component';
import { SuperAdminGuard } from './super-admin.guard';
import { TENANT_AUTHORIZATION_ROUTE_DATA_KEY, tenantAuthorizationGuard } from './tenant-authorization.guard';

const BILLING_ROUTE_DATA = {
  [TENANT_AUTHORIZATION_ROUTE_DATA_KEY]: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_BILLING,
};

const billingAuthorizationGuard: CanActivateFn = (route, state) => {
  if (!inject(AuthService).isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG)) {
    return true;
  }

  return tenantAuthorizationGuard(route, state);
};

const routes: Routes = [
  {
    path: 'password-setup',
    loadComponent: () => import('./auth/password-setup.component').then((m) => m.PasswordSetupComponent),
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthPageModule),
  },
  {
    path: 'profile',
    loadChildren: () => import('./profile/profile.module').then((m) => m.ProfilePageModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'profiles/:profileId',
    loadChildren: () => import('./profile/profile.module').then((m) => m.ProfilePageModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'people/:profileId',
    loadChildren: () => import('./profile/profile.module').then((m) => m.ProfilePageModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'people/:type/:profileId',
    loadChildren: () => import('./profile/profile.module').then((m) => m.ProfilePageModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'login/:authProvider',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthPageModule),
  },
  {
    path: 'oauth/login',
    loadChildren: () => import('./oauth/oauth-login.module').then((m) => m.OAuthLoginPageModule),
  },
  {
    path: 'upgrade',
    data: BILLING_ROUTE_DATA,
    loadChildren: () => import('./upgrade/upgrade.module').then((m) => m.UpgradePageModule),
    canActivate: [AuthGuard, billingAuthorizationGuard],
  },
  {
    path: 'super-admin',
    loadChildren: () => import('./super-admin/super-admin.module').then((m) => m.SuperAdminModule),
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'slack-message',
    loadChildren: () => import('./slack-message/slack-message.module').then((m) => m.SlackMessagePageModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'accounts',
    loadComponent: () => import('./accounts/accounts-list/accounts-list.page').then((m) => m.AccountsListPage),
    canActivate: [AuthGuard],
  },
  {
    path: 'channels',
    loadComponent: () => import('./channels/channel-list/channel-list.page').then((m) => m.ChannelListPage),
    canActivate: [AuthGuard],
  },
  {
    path: 'channels/new',
    loadChildren: () =>
      import('./customers/customer-create/customer-create.module').then((m) => m.CustomerCreatePageModule),
  },
  {
    path: 'upgrade',
    data: BILLING_ROUTE_DATA,
    loadChildren: () => import('./upgrade/upgrade.module').then((m) => m.UpgradePageModule),
    canActivate: [AuthGuard, billingAuthorizationGuard],
  },
  {
    path: 'subscription',
    data: BILLING_ROUTE_DATA,
    loadChildren: () => import('./upgrade/upgrade.module').then((m) => m.UpgradePageModule),
    canActivate: [AuthGuard, billingAuthorizationGuard],
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./cx-home/cx-home.module').then((m) => m.CxHomeModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'unsubscribe',
    loadChildren: () => import('./unsubscribe/unsubscribe.module').then((m) => m.UnsubscribeModule),
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./onboarding/onboarding.module').then((m) => m.OnboardingModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'install',
    loadChildren: () => import('./onboarding/onboarding.module').then((m) => m.OnboardingModule),
  },
  {
    path: 'submit-survey-response',
    loadComponent: () =>
      import('./components/survey-response/survey-response.component').then((m) => m.SurveyResponseComponent),
  },
  {
    path: 'close-window',
    component: CloseWindowComponent,
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, appRouterOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
